// feat_352 — Tab "Reporte por email" de /Settings (Fase 2, paso 2).
//
// Este bloque vivía al FINAL de `tabs/TabAutomatizaciones.js`. Salió a su propio
// tab por dos razones: (a) con la fusión del tab IA, Automatizaciones quedaba con
// 11 bloques, y (b) el reporte por email no tiene relación temática ni con el
// auto-cierre ni con la IA. El JSX es un MOVIMIENTO VERBATIM — no se restyleó
// nada; el patrón de feat_351 se le aplica en su propio paso.
//
// ⚠ GATE FUERTE — para la mayoría de las empresas este tab NO EXISTE:
//     getSettingValue("emailReportSuperadminAllowed") === "true" && userProfile === "admin"
// El gate se conserva acá adentro TAL CUAL estaba, y además el shell lo repite en
// `tabIsVisible` para no mostrar una pestaña que abriría en blanco (regla de
// feat_347: nunca un tab vacío). La duplicación es a propósito y es la misma que
// tiene TabPermisos: el gate del bloque y el de la pestaña se leen juntos.
//
// ⚠ `queues` (GET /queue) alimenta SÓLO a este bloque, pero se sigue pidiendo
// siempre desde el shell. Mover ese fetch acá sería una optimización con riesgo
// propio (el shell también lo usa para hidratar `emailReportTriggerQueueIds`) y
// queda FUERA de este paso, anotado en el reporte.
//
// 🔴 REGLA #1 DE ESTA PANTALLA: los 8 TabPanel quedan SIEMPRE MONTADOS (el shell
// los oculta con `hidden`, nunca los desmonta). 26 campos de texto guardan con
// `onBlur`; si al cambiar de tab React desmontara el input, el onBlur NO dispara
// y el usuario pierde lo tipeado sin ningún aviso. Este tab tiene 4 de esos
// campos (destinatarios, timeout, asunto y la plantilla HTML de 12 filas): NO
// convertirlo en render condicional ni en lazy.
//
// Todo el estado vive en el padre (`Settings/index.js`) y llega por `ctx`.

import React from "react";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RadioGroup from "@material-ui/core/RadioGroup";
import Radio from "@material-ui/core/Radio";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";

import api from "../../../services/api";
import { i18n } from "../../../translate/i18n.js";

const TabReporteEmail = ({ ctx }) => {
	const {
		saveSetting,
		getSettingValue,
		userProfile,
		// Reporte por email (gate: emailReportSuperadminAllowed + admin)
		emailReportEnabled, setEmailReportEnabled,
		emailReportRecipients, setEmailReportRecipients,
		emailReportTriggerQueueIds, setEmailReportTriggerQueueIds,
		emailReportTimeoutHours, setEmailReportTimeoutHours,
		emailReportTimerMode, setEmailReportTimerMode,
		emailReportSubjectTemplate, setEmailReportSubjectTemplate,
		emailReportHtmlTemplate, setEmailReportHtmlTemplate,
		queues,
		emailTestRecipient, setEmailTestRecipient,
		emailTestLoading, setEmailTestLoading,
		emailTestResult, setEmailTestResult,
	} = ctx;

	return (
		<>
			{/* Email Report — visible solo si superadmin lo habilitó y usuario es admin */}
			{getSettingValue("emailReportSuperadminAllowed") === "true" && userProfile === "admin" && (
				<Paper style={{ padding: 16, marginBottom: 12 }}>
					<Typography variant="subtitle2" gutterBottom>
						{i18n.t("settingsExtra.emailReportSectionTitle")}
					</Typography>
					<Typography variant="caption" color="textSecondary" display="block" gutterBottom>
						{i18n.t("settingsExtra.emailReportSectionDesc")}
					</Typography>
					<FormControlLabel
						control={
							<Switch
								checked={emailReportEnabled}
								onChange={async (e) => {
									setEmailReportEnabled(e.target.checked);
									await saveSetting("emailReportEnabled", e.target.checked ? "true" : "false");
								}}
								color="primary"
							/>
						}
						label={i18n.t("settingsExtra.activar")}
					/>
					<div style={{ opacity: emailReportEnabled ? 1 : 0.5, pointerEvents: emailReportEnabled ? "auto" : "none", marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
						{/* Queues disparadoras */}
						<div>
							<Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 4 }}>
								{i18n.t("settingsExtra.emailReportQueues")}
							</Typography>
							<Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8, fontStyle: "italic" }}>
								{i18n.t("settingsExtra.emailReportQueuesHelp")}
							</Typography>
							<div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
								{queues.map(q => (
									<FormControlLabel
										key={q.id}
										control={
											<Switch
												size="small"
												color="primary"
												checked={emailReportTriggerQueueIds.includes(q.id)}
												onChange={async (e) => {
													const newIds = e.target.checked
														? [...emailReportTriggerQueueIds, q.id]
														: emailReportTriggerQueueIds.filter(id => id !== q.id);
													setEmailReportTriggerQueueIds(newIds);
													await saveSetting("emailReportTriggerQueueIds", newIds.join(","));
												}}
											/>
										}
										label={q.name}
									/>
								))}
								{queues.length === 0 && (
									<Typography variant="caption" color="textSecondary">{i18n.t("settingsExtra.sinTipos")}</Typography>
								)}
							</div>
						</div>
						{/* Destinatarios */}
						<TextField
							label={i18n.t("settingsExtra.emailReportRecipients")}
							size="small"
							variant="outlined"
							fullWidth
							value={emailReportRecipients}
							onChange={e => setEmailReportRecipients(e.target.value)}
							onBlur={async () => { await saveSetting("emailReportRecipients", emailReportRecipients); }}
							helperText={i18n.t("settingsExtra.emailReportRecipientsHelp")}
						/>
						{/* Timeout */}
						<TextField
							label={i18n.t("settingsExtra.emailReportTimeoutHours")}
							type="number"
							size="small"
							variant="outlined"
							value={emailReportTimeoutHours}
							onChange={e => setEmailReportTimeoutHours(e.target.value)}
							onBlur={async () => { await saveSetting("emailReportTimeoutHours", emailReportTimeoutHours); }}
							inputProps={{ min: 1, max: 720 }}
							style={{ maxWidth: 300 }}
							helperText={i18n.t("settingsExtra.emailReportTimeoutHelp")}
						/>
						{/* Modo de timer (feat_196): desde updatedAt (A) o desde T0 del episodio (B) */}
						<FormControl component="fieldset">
							<FormLabel component="legend" style={{ fontSize: "0.85rem" }}>
								{i18n.t("emailReportTimerMode.label")}
							</FormLabel>
							<RadioGroup
								value={emailReportTimerMode}
								onChange={async e => {
									const mode = e.target.value;
									setEmailReportTimerMode(mode);
									await saveSetting("emailReportTimerMode", mode);
								}}
							>
								<FormControlLabel
									value="updatedAt"
									control={<Radio color="primary" size="small" />}
									label={i18n.t("emailReportTimerMode.updatedAt")}
								/>
								<Typography variant="caption" color="textSecondary" style={{ marginLeft: 32, marginTop: -4, marginBottom: 8 }}>
									{i18n.t("emailReportTimerMode.updatedAtHelp")}
								</Typography>
								<FormControlLabel
									value="createdAt"
									control={<Radio color="primary" size="small" />}
									label={i18n.t("emailReportTimerMode.createdAt")}
								/>
								<Typography variant="caption" color="textSecondary" style={{ marginLeft: 32, marginTop: -4 }}>
									{i18n.t("emailReportTimerMode.createdAtHelp", { hours: emailReportTimeoutHours })}
								</Typography>
							</RadioGroup>
						</FormControl>
						{/* Asunto */}
						<TextField
							label={i18n.t("settingsExtra.emailReportSubjectTemplate")}
							size="small"
							variant="outlined"
							fullWidth
							value={emailReportSubjectTemplate}
							onChange={e => setEmailReportSubjectTemplate(e.target.value)}
							onBlur={async () => { await saveSetting("emailReportSubjectTemplate", emailReportSubjectTemplate); }}
							placeholder={i18n.t("settingsExtra.emailReportSubjectPlaceholder")}
						/>
						{/* Plantilla HTML */}
						<TextField
							label={i18n.t("settingsExtra.emailReportHtmlTemplate")}
							multiline
							rows={12}
							variant="outlined"
							fullWidth
							value={emailReportHtmlTemplate}
							onChange={e => setEmailReportHtmlTemplate(e.target.value)}
							onBlur={async () => { await saveSetting("emailReportHtmlTemplate", emailReportHtmlTemplate); }}
							helperText={i18n.t("settingsExtra.emailReportHtmlHelp")}
							inputProps={{ style: { fontFamily: "monospace", fontSize: 12 } }}
						/>
						{/* Variables disponibles */}
						<Accordion elevation={0} style={{ border: "1px solid #e0e0e0", borderRadius: 4 }}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography variant="caption" color="textSecondary">
									{i18n.t("settingsExtra.emailReportVarsTitle")}
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Typography variant="caption" color="textSecondary" style={{ fontFamily: "monospace", lineHeight: 1.8, whiteSpace: "pre-wrap" }}>
									{i18n.t("settingsExtra.emailReportVarsDesc")}
								</Typography>
							</AccordionDetails>
						</Accordion>
						{/* Enviar prueba */}
						<div>
							<Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 8 }}>
								{i18n.t("settingsExtra.emailReportTestLabel")}
							</Typography>
							<div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
								<TextField
									size="small"
									variant="outlined"
									type="email"
									value={emailTestRecipient}
									onChange={e => { setEmailTestRecipient(e.target.value); setEmailTestResult(null); }}
									placeholder="test@ejemplo.com"
									style={{ flex: 1 }}
								/>
								<Button
									variant="contained"
									color="primary"
									size="small"
									disabled={emailTestLoading || !emailTestRecipient.trim()}
									onClick={async () => {
										setEmailTestLoading(true);
										setEmailTestResult(null);
										try {
											await api.post("/settings/email-report/test", { testRecipient: emailTestRecipient.trim() });
											setEmailTestResult({ ok: true, msg: i18n.t("settingsExtra.emailReportTestSuccess").replace("{email}", emailTestRecipient.trim()) });
										} catch (err) {
											const errMsg = err?.response?.data?.error || err.message || "unknown";
											setEmailTestResult({ ok: false, msg: i18n.t("settingsExtra.emailReportTestError").replace("{error}", errMsg) });
										} finally {
											setEmailTestLoading(false);
										}
									}}
								>
									{emailTestLoading ? i18n.t("settingsExtra.emailReportTestSending") : i18n.t("settingsExtra.emailReportTestBtn")}
								</Button>
							</div>
							{emailTestResult && (
								<Typography
									variant="caption"
									display="block"
									style={{ marginTop: 6, color: emailTestResult.ok ? "#2e7d32" : "#c62828" }}
								>
									{emailTestResult.msg}
								</Typography>
							)}
						</div>
					</div>
				</Paper>
			)}
		</>
	);
};

export default TabReporteEmail;
